import React, {
	createContext,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { collection, query, onSnapshot } from 'firebase/firestore';
import { firestore } from '../utils/firebase';
import { useDispatch, useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import {
	clearFirebaseToken,
	setFirebaseReady,
	setFirebaseTokenEnabled,
} from '@/features/authorisationSlice/authorisationSlice';

const initialState = {
	featureFlags: [],
	getFeatureFlags: (feature) => {},
	isLoading: true,
	isUninitialized: true,
	isError: false,
	isSuccess: false,
};

const mapFeatureDoc = (id, doc) => ({
	id,
	feature: doc.feature,
	description: doc.description ?? '',
	userIds: (doc.users ?? []).map((v) => v.id),
	brokerGroupIds: (doc.brokerGroups ?? []).map((v) => v.id),
	ledgers: doc.ledgers ?? [],
	createdWhen: doc.createdWhen,
	active: doc.active,
});

const FeatureFlagContext = createContext(initialState);

export const useFeatureFlagContext = () => {
	const context = useContext(FeatureFlagContext);
	if (!context) {
		throw new Error('useFeatureFlagContext must be used within a FeatureFlagProvider');
	}
	return context;
};

export const FeatureFlagProvider = ({ children }) => {
	const dispatch = useDispatch();
	const [isLoading, setIsLoading] = useState(true);

	const isUninitializedRef = useRef(true);

	const [isError, setIsError] = useState(false);
	const isSuccess = useMemo(
		() => !isError && !isUninitializedRef.current && !isLoading,
		[isError, isLoading],
	);

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;
	const firebaseReady = useSelector((state) => state.firebase.firebaseReady);
	const { currentData: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email || !firebaseReady,
		},
	);

	const userContextRef = useRef({
		userId: userDetails?.userId,
		brokerGroupArray: userDetails?.brokerGroupArray ?? [],
		ledger: userDetails?.broker?.ledger,
		email,
	});

	useEffect(() => {
		userContextRef.current = {
			userId: userDetails?.userId,
			brokerGroupArray: userDetails?.brokerGroupArray ?? [],
			ledger: userDetails?.broker?.ledger,
			email,
		};
	}, [userDetails?.userId, userDetails?.brokerGroupArray, userDetails?.broker?.ledger, email]);

	const [featureFlags, setFeatureFlags] = useState([]);
	// null = not yet settled, so the first real determination always dispatches
	// even if it happens to resolve to `false`.
	const prevFirebaseTokenRequiredRef = useRef(null);

	const isFeatureUserEnabled = (doc) => {
		const { userId } = userContextRef.current;
		return (doc.userIds ?? []).includes(userId);
	};

	const isFeatureBrokerGroupEnabled = (doc) => {
		const { brokerGroupArray } = userContextRef.current;
		return (doc.brokerGroupIds ?? []).some((r) => brokerGroupArray.includes(r));
	};

	const isFeatureLedgerEnabled = (doc) => {
		const { ledger } = userContextRef.current;
		return (doc.ledgers ?? []).some((r) => r === ledger);
	};

	const isFeatureActiveForUser = (doc) => {
		if (!doc) return false;
		return (
			isFeatureLedgerEnabled(doc) ||
			isFeatureBrokerGroupEnabled(doc) ||
			isFeatureUserEnabled(doc)
		);
	};

	const isFeatureActive = (doc) => doc?.active || isFeatureActiveForUser(doc);

	// Flag off → BAU (ready, no Authorization). Flag on → wait for token before ready.
	useEffect(() => {
		if (isLoading) {
			return;
		}

		const firebaseTokenFlag = featureFlags.find((doc) => doc.feature === 'firebaseToken');
		// Before user is known, only trust global active; after, include user/ledger targeting.
		const requiresFirebaseToken = userDetails?.userId
			? isFeatureActive(firebaseTokenFlag)
			: Boolean(firebaseTokenFlag?.active);

		// Nothing actually changed since we last settled — skip re-dispatching.
		// Without this, userDetails toggling in and out of existence (it's gated
		// on firebaseReady, which this effect also sets) re-ran this effect on
		// every settle, and re-triggered firebaseTokenContext's mint effect for
		// no reason.
		if (requiresFirebaseToken === prevFirebaseTokenRequiredRef.current) {
			return;
		}

		dispatch(setFirebaseTokenEnabled(requiresFirebaseToken));

		if (!requiresFirebaseToken) {
			dispatch(clearFirebaseToken());
			dispatch(setFirebaseReady(true));
		} else {
			// Just turned on — block until FirebaseTokenProvider mints/refreshes the session.
			dispatch(setFirebaseReady(false));
		}

		prevFirebaseTokenRequiredRef.current = requiresFirebaseToken;
	}, [featureFlags, isLoading, userDetails?.userId, dispatch]);

	useEffect(() => {
		if (isUninitializedRef.current) {
			isUninitializedRef.current = false;
		}

		const q = query(collection(firestore, 'featureFlags'));

		const unsubscribe = onSnapshot(q, (snapshot) => {
			try {
				setFeatureFlags((prev) => {
					let updated = [...prev];

					snapshot.docChanges().forEach((change) => {
						const doc = change.doc.data();
						const id = change.doc.id;

						if (change.type === 'removed') {
							updated = updated.filter((f) => f.id !== id);
						} else if (change.type === 'added' || change.type === 'modified') {
							const feature = mapFeatureDoc(id, doc);
							const index = updated.findIndex((f) => f.id === id);
							if (index !== -1) {
								updated[index] = feature;
							} else {
								updated.push(feature);
							}
						}
					});
					return updated;
				});
			} catch (e) {
				console.error('Error getting feature flags from Firebase', e);
				setIsError(true);
			} finally {
				setIsLoading(false);
			}
		}, (error) => {
			console.error('Error getting feature flags from Firebase', error);
			setIsError(true);
			setIsLoading(false);
		});

		return () => unsubscribe();
	}, []);

	const getFeatureFlags = (feature) => {
		const { userId } = userContextRef.current;
		let docs = [...(featureFlags ?? []).slice()];

		if (feature && !userId) {
			return docs.find((doc) => doc.feature === feature);
		}

		docs = docs.map((doc) => ({
			...doc,
			active: isFeatureActive(doc),
		}));
		return feature ? docs.find((doc) => doc.feature === feature) : docs;
	};
	return (
		<FeatureFlagContext.Provider
			value={{
				featureFlags,
				getFeatureFlags,
				isLoading,
				isError,
				isUninitialized: isUninitializedRef.current,
				isSuccess,
			}}
		>
			{children}
		</FeatureFlagContext.Provider>
	);
};
